import {
  AlertTriangle,
  Check,
  CreditCard,
  LayoutDashboard,
  Settings,
  Shield,
  Users,
  X,
} from "lucide-react";
import { FormEvent, ReactNode, useEffect, useState } from "react";
import { Brand } from "@/components/brand";
import { api, csrf } from "./api";

type View = "dashboard" | "moderation" | "settings";
type Metrics = {
  users: number;
  active_posts: number;
  open_dates: number;
  open_reports: number;
  active_subscriptions: number;
};
type Square = {
  environment: "sandbox" | "production";
  sandbox: Record<string, string>;
  production: Record<string, string>;
  plan_variations: Record<string, string>;
};
const blankSquare: Square = {
  environment: "sandbox",
  sandbox: {
    application_id: "",
    access_token: "",
    location_id: "",
    webhook_signature_key: "",
  },
  production: {
    application_id: "",
    access_token: "",
    location_id: "",
    webhook_signature_key: "",
  },
  plan_variations: { plus: "", ultimate: "" },
};

export function AdminPage() {
  const [authenticated, setAuthenticated] = useState<boolean | null>(null);
  const [view, setView] = useState<View>("dashboard");
  const [metrics, setMetrics] = useState<Metrics>({
    users: 0,
    active_posts: 0,
    open_dates: 0,
    open_reports: 0,
    active_subscriptions: 0,
  });
  const [square, setSquare] = useState<Square>(blankSquare);
  const [notice, setNotice] = useState("");
  useEffect(() => {
    api
      .get("/admin/dashboard")
      .then(({ data }) => {
        setMetrics(data.data);
        setAuthenticated(true);
      })
      .catch(() => setAuthenticated(false));
  }, []);
  useEffect(() => {
    if (authenticated && view === "settings")
      api
        .get("/admin/settings")
        .then(({ data }) =>
          setSquare((old) => ({ ...old, ...(data.data.square ?? {}) })),
        );
  }, [authenticated, view]);
  if (authenticated === null)
    return (
      <main className="auth-layout">
        <p className="eyebrow">Opening secure administration…</p>
      </main>
    );
  if (!authenticated)
    return (
      <AdminLogin
        onSuccess={(data) => {
          setMetrics(data);
          setAuthenticated(true);
        }}
      />
    );
  const saveSquare = async () => {
    setNotice("Saving…");
    try {
      await api.put("/admin/settings", { key: "square", value: square });
      setNotice("Square settings saved encrypted.");
    } catch {
      setNotice("Settings could not be saved. Check each required value.");
    }
  };
  return (
    <main className="admin-app">
      <aside className="admin-side">
        <Brand />
        <p className="eyebrow" style={{ marginTop: "1.5rem" }}>
          Administration
        </p>
        <nav>
          <Nav
            active={view === "dashboard"}
            onClick={() => setView("dashboard")}
            icon={<LayoutDashboard />}
          >
            Dashboard
          </Nav>
          <Nav
            active={view === "moderation"}
            onClick={() => setView("moderation")}
            icon={<Shield />}
          >
            Moderation
          </Nav>
          <Nav
            active={view === "settings"}
            onClick={() => setView("settings")}
            icon={<Settings />}
          >
            Integrations
          </Nav>
        </nav>
      </aside>
      <section className="admin-workspace">
        {view === "dashboard" && <Dashboard metrics={metrics} />}
        {view === "moderation" && <Moderation />}
        {view === "settings" && (
          <>
            <p className="eyebrow">Encrypted configuration</p>
            <h1 className="page-title">External integrations</h1>
            <p className="muted">
              Secrets are encrypted at rest and are redacted after saving.
              Environment variables remain the safe fallback.
            </p>
            <div className="admin-form-grid">
              <section className="publish-preview">
                <h2 className="section-title">Square</h2>
                <div className="choice-grid">
                  <button
                    className={`choice ${square.environment === "sandbox" ? "selected" : ""}`}
                    onClick={() =>
                      setSquare({ ...square, environment: "sandbox" })
                    }
                  >
                    Sandbox
                  </button>
                  <button
                    className={`choice ${square.environment === "production" ? "selected" : ""}`}
                    onClick={() =>
                      setSquare({ ...square, environment: "production" })
                    }
                  >
                    Production
                  </button>
                </div>
                <SquareFields
                  environment={square.environment}
                  value={square[square.environment]}
                  onChange={(key, value) =>
                    setSquare({
                      ...square,
                      [square.environment]: {
                        ...square[square.environment],
                        [key]: value,
                      },
                    })
                  }
                />
                <label className="field">
                  <span>Plus plan variation ID</span>
                  <input
                    value={square.plan_variations.plus ?? ""}
                    onChange={(e) =>
                      setSquare({
                        ...square,
                        plan_variations: {
                          ...square.plan_variations,
                          plus: e.target.value,
                        },
                      })
                    }
                  />
                </label>
                <label className="field">
                  <span>Ultimate plan variation ID</span>
                  <input
                    value={square.plan_variations.ultimate ?? ""}
                    onChange={(e) =>
                      setSquare({
                        ...square,
                        plan_variations: {
                          ...square.plan_variations,
                          ultimate: e.target.value,
                        },
                      })
                    }
                  />
                </label>
                <button className="button button-primary" onClick={saveSquare}>
                  Save encrypted settings
                </button>
                {notice && (
                  <p role="status" className="muted">
                    {notice}
                  </p>
                )}
              </section>
            </div>
          </>
        )}
      </section>
    </main>
  );
}

function AdminLogin({ onSuccess }: { onSuccess: (metrics: Metrics) => void }) {
  const [error, setError] = useState("");
  const submit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const fields = new FormData(e.currentTarget);
    try {
      await csrf();
      await api.post("/admin/auth/login", Object.fromEntries(fields));
      const { data } = await api.get("/admin/dashboard");
      onSuccess(data.data);
    } catch {
      setError(
        "Email, password, or the six-digit authenticator code is incorrect.",
      );
    }
  };
  return (
    <main className="auth-layout">
      <section className="auth-panel">
        <Brand />
        <p className="eyebrow">Protected administration</p>
        <h1 className="page-title">Sign in with two factors.</h1>
        <form onSubmit={submit}>
          <label className="field">
            <span>Email</span>
            <input name="email" type="email" required />
          </label>
          <label className="field">
            <span>Password</span>
            <input name="password" type="password" required />
          </label>
          <label className="field">
            <span>Authenticator code</span>
            <input
              name="code"
              inputMode="numeric"
              pattern="[0-9]{6}"
              maxLength={6}
              required
            />
          </label>
          {error && (
            <p role="alert" className="form-error">
              {error}
            </p>
          )}
          <button className="button button-primary button-block">
            Open administration
          </button>
        </form>
      </section>
    </main>
  );
}
function Dashboard({ metrics }: { metrics: Metrics }) {
  return (
    <>
      <p className="eyebrow">Platform overview</p>
      <h1 className="page-title">What needs attention.</h1>
      <div className="metric-grid">
        <Metric value={metrics.users} label="Members" />
        <Metric value={metrics.active_posts} label="Active Posts" />
        <Metric value={metrics.open_dates} label="Open Dates" />
        <Metric value={metrics.open_reports} label="Reports" accent />
      </div>
      <div className="moderation-grid">
        <div className="queue">
          <h2 className="section-title">Priority</h2>
          <p className="muted">Review exceptions, not every conversation.</p>
        </div>
        <div className="review-content">
          <p className="eyebrow">System health</p>
          <h2 className="section-title">Core services configured</h2>
          <p className="muted">MySQL · Mail · Private storage · Square</p>
        </div>
        <div className="decision-panel">
          <CreditCard />
          <h2 className="section-title">Subscriptions</h2>
          <p className="muted">{metrics.active_subscriptions} active</p>
        </div>
      </div>
    </>
  );
}
function Moderation() {
  const [decision, setDecision] = useState("Awaiting review");
  return (
    <>
      <p className="eyebrow">Exception-based review</p>
      <h1 className="page-title">Moderation workspace</h1>
      <div className="moderation-grid" style={{ marginTop: "2rem" }}>
        <div className="queue">
          <span className="tag tag-warm">
            <AlertTriangle /> High risk
          </span>
          <button className="queue-row">
            <strong>Post awaiting review</strong>
            <span className="muted" style={{ display: "block" }}>
              Commercial wording
            </span>
          </button>
        </div>
        <article className="review-content">
          <p className="eyebrow">Content under review</p>
          <h2 className="section-title">Private booking tonight</h2>
          <p className="detail-copy">
            Automated scanning found service-like language and external contact
            details.
          </p>
          <div className="fact-list">
            <div className="fact-row">
              <Users />
              <strong>Account history is included</strong>
            </div>
          </div>
        </article>
        <aside className="decision-panel">
          <p className="eyebrow">Decision</p>
          <h2 className="section-title">{decision}</h2>
          <button
            className="button button-secondary button-block"
            onClick={() => setDecision("Approved and logged")}
          >
            <Check /> Approve
          </button>
          <button
            className="button button-primary button-block"
            style={{ marginTop: ".5rem" }}
            onClick={() => setDecision("Rejected and user warned")}
          >
            <X /> Reject
          </button>
        </aside>
      </div>
    </>
  );
}
function SquareFields({
  environment,
  value,
  onChange,
}: {
  environment: string;
  value: Record<string, string>;
  onChange: (key: string, value: string) => void;
}) {
  return (
    <>
      <label className="field">
        <span>
          {environment === "sandbox" ? "Sandbox" : "Production"} application ID
        </span>
        <input
          value={value.application_id ?? ""}
          onChange={(e) => onChange("application_id", e.target.value)}
        />
      </label>
      <label className="field">
        <span>
          {environment === "sandbox" ? "Sandbox" : "Production"} access token
        </span>
        <input
          type="password"
          value={value.access_token ?? ""}
          placeholder="Leave redacted value unchanged"
          onChange={(e) => onChange("access_token", e.target.value)}
        />
      </label>
      <label className="field">
        <span>Location ID</span>
        <input
          value={value.location_id ?? ""}
          onChange={(e) => onChange("location_id", e.target.value)}
        />
      </label>
      <label className="field">
        <span>Webhook signature key</span>
        <input
          type="password"
          value={value.webhook_signature_key ?? ""}
          onChange={(e) => onChange("webhook_signature_key", e.target.value)}
        />
      </label>
    </>
  );
}
function Nav({
  active,
  onClick,
  icon,
  children,
}: {
  active: boolean;
  onClick: () => void;
  icon: ReactNode;
  children: ReactNode;
}) {
  return (
    <button className={active ? "active" : ""} onClick={onClick}>
      {icon}
      {children}
    </button>
  );
}
function Metric({
  value,
  label,
  accent = false,
}: {
  value: number;
  label: string;
  accent?: boolean;
}) {
  return (
    <div className="metric">
      <span className="muted">{label}</span>
      <strong
        style={{ color: accent ? "var(--color-accent-warm)" : undefined }}
      >
        {value.toLocaleString()}
      </strong>
      <small className="muted">Live total</small>
    </div>
  );
}
